<script setup lang="ts">
import type { Swiper as SwiperClass } from 'swiper/types';
import { Swiper, SwiperSlide } from 'swiper/vue';
import { Navigation, Mousewheel } from 'swiper';
import { breakpoints } from '#shared/lib/breakpoints';
import {
  CloseIcon,
  LeftArrowIcon,
  RightArrowIcon,
} from '#shared/components/icons';
import { useBodyScrolling } from '#shared/store/bodyScrolling';
import { watch, ref } from 'vue';

const props = withDefaults(
  defineProps<{
    modelValue?: boolean;
    imageUrls?: string[];
    initialImage?: number;
    loop?: boolean;
  }>(),
  {
    imageUrls: () => [],
    initialImage: 0,
    loop: true,
  }
);
const emit = defineEmits<{
  (e: 'update:model-value', modelValue: boolean): void;
}>();

const { enableBodyScrolling, disableBodyScrolling } = useBodyScrolling();
watch(
  () => props.modelValue,
  (value) => {
    if (value) disableBodyScrolling();
    else enableBodyScrolling();
  }
);

const topSwiper = ref<SwiperClass>();
const thumbSwiper = ref<SwiperClass>();

function setTopSwiper(swiper: SwiperClass) {
  return (topSwiper.value = swiper);
}
function setThumbSwiper(swiper: SwiperClass) {
  return (thumbSwiper.value = swiper);
}
function onTopSlideChange() {
  if (!thumbSwiper.value || !topSwiper.value) return;
  thumbSwiper.value.slideToLoop(topSwiper.value.realIndex, undefined, false);
}
function onThumbSlideChange() {
  if (!thumbSwiper.value || !topSwiper.value) return;
  topSwiper.value.slideToLoop(thumbSwiper.value.realIndex, undefined, false);
}
function onClose() {
  return emit('update:model-value', false);
}
function onSliderDestroy() {
  topSwiper.value = undefined;
  thumbSwiper.value = undefined;
}
</script>

<template>
  <ClientOnly>
    <Teleport to="body">
      <Transition name="lightbox">
        <div
          v-if="props.modelValue"
          class="lightbox__wrapper"
          @keydown.esc="onClose()"
          @click="onClose()"
        >
          <div class="lightbox__close" @click="onClose()">
            <CloseIcon />
          </div>

          <div class="lightbox__body" tabindex="0" @click.stop>
            <Swiper
              :modules="[Navigation, Mousewheel]"
              :navigation="{
                prevEl: '.top-slider__prev',
                nextEl: '.top-slider__next',
              }"
              mousewheel
              :initial-slide="initialImage"
              :space-between="10"
              :loop="loop"
              class="top-slider"
              @swiper="setTopSwiper"
              @slide-change="onTopSlideChange"
              @destroy="onSliderDestroy"
            >
              <SwiperSlide
                v-for="(url, index) in props.imageUrls"
                :key="index"
                class="top-slider__slide"
              >
                <img :src="url" draggable="false" />
              </SwiperSlide>
              <template v-if="props.imageUrls.length > 1">
                <AppButton
                  class="top-slider__prev"
                  variant="link"
                  :icon-end="LeftArrowIcon"
                />
                <AppButton
                  class="top-slider__next"
                  variant="link"
                  :icon-end="RightArrowIcon"
                />
              </template>
            </Swiper>
            <Swiper
              v-show="props.imageUrls.length > 1"
              :breakpoints="{
                [breakpoints['xs']]: {
                  slidesPerView: 3,
                  spaceBetween: 10,
                },
                [breakpoints['md']]: {
                  slidesPerView: 5,
                  spaceBetween: 10,
                },
                [breakpoints['lg']]: {
                  slidesPerView: 8,
                  spaceBetween: 10,
                },
              }"
              :initial-slide="initialImage"
              :threshold="5"
              :loop="loop"
              centered-slides
              watch-slides-progress
              slide-to-clicked-slide
              class="thumb-slider"
              @swiper="setThumbSwiper"
              @slide-change="onThumbSlideChange"
              @destroy="onSliderDestroy"
            >
              <SwiperSlide v-for="(url, index) in props.imageUrls" :key="index">
                <img :src="url" />
              </SwiperSlide>
            </Swiper>
          </div>
        </div>
      </Transition>
    </Teleport>
  </ClientOnly>
</template>

<style lang="scss" scoped>
@import '#shared/assets/core.scss';

.lightbox {
  &-enter-active,
  &-leave-active {
    transition: opacity 0.25s ease-out;
  }

  &-enter-from,
  &-leave-to {
    opacity: 0;
  }

  &__wrapper {
    display: flex;
    justify-content: center;
    align-items: center;
    position: fixed;
    left: 0;
    top: 0;
    width: 100vw;
    height: 100vh;
    z-index: 102;
    background-color: rgba(20, 20, 20, 90%);
  }

  &__body {
    width: 100%;
    padding: 1.5rem;
    overflow: hidden;
    max-height: 90vh;

    @include media-breakpoint-down(md) {
      flex: 1;
      position: fixed;
      bottom: 0;
      left: 0;
      right: 0;
      width: 100%;
      height: 90vh;
      overflow-x: hidden;
      overflow-y: auto;
      border-end-end-radius: 0;
      border-end-start-radius: 0;
    }
  }

  &__close {
    width: 48px;
    height: 48px;
    cursor: pointer;
    color: var(--app-color-white);
    position: absolute;
    top: 1em;
    right: 1em;
    z-index: 102;
  }
}

.top-slider {
  width: 100%;
  height: 65vh;

  &__slide {
    padding: 0;
    height: 100%;
    margin-left: auto;
    margin-right: auto;

    & img {
      width: 100%;
      height: 100%;
      object-fit: contain;
      user-select: none;
    }
  }

  &__prev,
  &__next {
    position: absolute;
    top: 50%;
    z-index: 5;
    padding: 10px !important;
    color: var(--app-color-tertiary);
    background-color: var(--app-color-white) !important;
    border: none;
    border-radius: 50% !important;

    @include media-breakpoint-down(md) {
      display: none;
    }

    &:hover {
      color: var(--app-color-primary);
    }

    svg {
      width: 28px;
      height: 28px;
    }
  }

  &__prev {
    left: 15px;
  }

  &__next {
    right: 15px;
  }
}

.thumb-slider {
  width: 50vw;
  height: 10vh;
  margin: 3em auto 0 auto;

  @include media-breakpoint-down(xl) {
    width: 80vw;
  }
}
</style>

<style lang="scss">
.thumb-slider .swiper-slide {
  opacity: 0.4;
  cursor: pointer;

  &-active {
    opacity: 1;
  }

  & img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 8px;
  }
}
</style>
